import React from 'react';
import PropTypes from 'prop-types';
import { exportGif } from '../image_utilities/ExportGif';
import { imageShape } from '../shapes/ImageShapes';
import Player from './Player';
import CanvasViewport from './CanvasViewport';
import { mod } from '../image_utilities/Math';
import { calculateTimeline, planTimeline, timelineStartTimes, parseDurationSeconds } from '../image_utilities/Timeline';
import DraggableOverlayLayer from './DraggableOverlayLayer';
import isEqual from 'react-fast-compare';
import SaveDialog from './SaveDialog';
import GifPreview from './GifPreview';
import { getColorFromPixel } from '../image_utilities/Recolor';
import { DEFAULT_TRANSPARENCY_TOLERANCE } from '../constants/export_constants';

const styles = {
  canvas: {
    border: 'solid #000',
    borderWidth: '1px',
    position: 'relative',
  },
};

class AnimatedCanvas extends React.Component {
  state = {
    timeout: null,
    isSaving: false,
    saveProgress: 0,
    saveError: null,
    isPlaying: this.props.initialIsPlaying,
    isMoveMode: false,
    hoveredImageName: null,
    draggedImageName: null,
    absoluteCurrentIndex: 0,
    transparentColor: '',
    transparencyTolerance: DEFAULT_TRANSPARENCY_TOLERANCE,
    saveImageName: '',
    keepPreviousFrames: false,
    durationInput: this.props.initialOutputOptions.durationInput,
    playbackMode: this.props.initialOutputOptions.playbackMode,
    playbackEnded: false,
  };

  canvasRef = React.createRef();

  // Cache for image dimensions: WeakMap<Image, {minX, minY, width, height}>
  imageDimensionsCache = new WeakMap();
  timelineCache = null;
  exportController = null;

  componentDidMount = () => {
    this.setState(
      {
        absoluteCurrentIndex: 0,
        timeout: null,
      },
      () => {
        this.updateCanvas();
        if (this.isPlaying()) {
          this.requestTimeout();
        }
      }
    );
  };

  componentWillUnmount = () => {
    this.exportController?.abort();
    this.exportController = null;
    this.cancelTimeout();
  };

  componentDidUpdate = (prevProps, prevState) => {
    if (!isEqual(prevProps.initialOutputOptions, this.props.initialOutputOptions) &&
      (this.state.durationInput !== this.props.initialOutputOptions.durationInput ||
        this.state.playbackMode !== this.props.initialOutputOptions.playbackMode)) {
      this.setState(this.props.initialOutputOptions);
      return;
    }
    if (prevState.isPlaying !== this.state.isPlaying) {
      this.props.onPlaybackChange(this.state.isPlaying);
    }
    const contentChanged =
      prevProps.compositionMode !== this.props.compositionMode ||
      !isEqual(this.props.images, prevProps.images) ||
      prevProps.width !== this.props.width ||
      prevProps.height !== this.props.height ||
      prevProps.backgroundColor !== this.props.backgroundColor ||
      prevState.durationInput !== this.state.durationInput ||
      prevState.playbackMode !== this.state.playbackMode;

    if (contentChanged) {
      this.cancelTimeout();
      // An edit resets the timeline, but preserves the user's playback choice.
      this.setState({ absoluteCurrentIndex: 0, playbackEnded: false }, () => {
        this.updateCanvas();
        this.requestTimeout();
      });
    } else if (
      prevProps.canPlay !== this.props.canPlay ||
      prevState.isPlaying !== this.state.isPlaying
    ) {
      // Tool pauses and the play button only change scheduling, not the current frame.
      this.cancelTimeout(this.requestTimeout);
    }

    if (
      this.props.selectedPixel.length > 0 &&
      prevProps.selectedPixel !== this.props.selectedPixel
    ) {
      // If we have a new pixel, the eyedropper must have selected a color
      this.setState({ transparentColor: getColorFromPixel(this.props.selectedPixel) });
    }
  };

  cancelTimeout = (callback) => {
    cancelAnimationFrame(this.state.timeout);
    this.setState({ timeout: null }, callback);
  };

  requestTimeout = () => {
    const start = new Date().getTime();
    const delay = this.getCurrentMaxDelay();
    if (this.isPlaying() && this.state.timeout === null && this.getTimeline().length &&
      (this.getMaxFrames() > 1 || this.playsOnce())) {
      this.createTimeout(start + delay);
    }
  };

  createTimeout = (end) => {
    const timeout = requestAnimationFrame(this.getTimeoutCallback(end));
    this.setState({ timeout });
  };

  getTimeoutCallback = (end) => {
    return () => {
      const current = new Date().getTime();
      if (this.isPlaying() && current >= end) {
        const timeline = this.getTimeline();
        const starts = timelineStartTimes(timeline);
        const duration = starts[starts.length - 1] + timeline[timeline.length - 1].delay;
        const elapsed = starts[this.state.absoluteCurrentIndex] + this.getCurrentMaxDelay() + current - end;
        const finished = this.playsOnce() && elapsed >= duration;
        const time = finished ? duration - 1 : elapsed % duration;
        const following = starts.findIndex(start => start > time);
        const index = following === -1 ? timeline.length - 1 : following - 1;
        this.setState(
          { absoluteCurrentIndex: index, timeout: null,
            ...(finished ? { isPlaying: false, playbackEnded: true } : {}) },
          () => {
            this.updateCanvas();
            if (!finished && this.isPlaying()) {
              this.createTimeout(current + starts[index] + timeline[index].delay - time);
            }
          }
        );
      } else if (this.isPlaying()) {
        // loop
        this.createTimeout(end);
      } else {
        this.cancelTimeout();
      }
    };
  };

  updateCanvas = () => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');
    this.clearCanvas();
    this.drawBackground();
    this.getCurrentFrames().forEach((frame, index) => {
      const curImage = this.props.images[index];
      // Only spend resources to draw a real image
      if (frame && !frame.isEmpty) {
        ctx.drawImage(
          frame.image,
          curImage.x + frame.x,
          curImage.y + frame.y,
          frame.width,
          frame.height
        );
      }
    });
  };

  clearCanvas = () => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');
    // clear canvas
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  };

  drawBackground = () => {
    const canvas = this.canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = this.props.backgroundColor;
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
  };

  handleMouseMove = (e) => {
    // Optimization: Skip expensive pixel reading (getBoundingClientRect + getImageData)
    // if the eye dropper is not active, as we don't need pixel data.
    if (!this.props.showEyeDropperCursor) {
      this.props.onMouseMove(e, new Uint8ClampedArray());
      return;
    }

    const canvas = this.canvasRef.current;
    const rect = canvas.getBoundingClientRect();

    const x = ((e.clientX - rect.left) / (rect.right - rect.left)) * canvas.width;
    const y = ((e.clientY - rect.top) / (rect.bottom - rect.top)) * canvas.height;

    const ctx = canvas.getContext('2d');

    const pixel = ctx.getImageData(x, y, 1, 1);
    this.props.onMouseMove(e, pixel.data);
  };

  handleSaveSubmit = async () => {
    if (this.exportController) return;
    if (this.getTimelineModel().error) {
      this.setState({ saveError: this.getTimelineModel().error });
      return;
    }
    const controller = new AbortController();
    this.exportController = controller;
    const { images, width, height, backgroundColor } = this.props;
    const { transparentColor, transparencyTolerance, keepPreviousFrames, saveImageName } = this.state;
    this.setState({ isSaving: true, saveProgress: 0, saveError: null });
    try {
      const blob = await exportGif({
        images, timeline: this.getTimeline(), width, height, backgroundColor,
        transparentColor, transparencyTolerance, keepPreviousFrames, signal: controller.signal,
        repeat: this.playsOnce() ? -1 : 0,
        onProgress: saveProgress => {
          if (!controller.signal.aborted) this.setState({ saveProgress });
        },
      });
      if (controller.signal.aborted) return;
      const element = document.createElement('a');
      const url = URL.createObjectURL(blob);
      element.href = url;
      element.download = `${saveImageName || 'myPeamoji'}.gif`;
      element.click();
      // Keep the URL alive until the browser has started the download.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      this.setState({ shouldShowSaveDialog: false });
    } catch (error) {
      if (!controller.signal.aborted) {
        this.setState({ saveError: 'Could not save this GIF. Please try again.' });
      }
    } finally {
      if (this.exportController === controller) {
        this.exportController = null;
        this.setState({ isSaving: false });
      }
    }
  };

  getTimelineModel = () => {
    if (
      this.timelineCache &&
      this.timelineCache.compositionMode === this.props.compositionMode &&
      this.timelineCache.durationInput === this.state.durationInput &&
      this.timelineCache.images.length === this.props.images.length &&
      this.timelineCache.images.every(
        (img, i) =>
          img.frames === this.props.images[i].frames &&
          img.startIndex === this.props.images[i].startIndex &&
          img.playCount === this.props.images[i].playCount &&
          img.endBehavior === this.props.images[i].endBehavior
      )
    ) {
      return this.timelineCache.model;
    }

    const options = this.state.durationInput.trim() === '' ? {} : {
      durationMs: parseDurationSeconds(this.state.durationInput),
    };
    options.compositionMode = this.props.compositionMode;
    const plan = planTimeline(this.props.images, options);
    let timeline = [];
    let error = plan.error;
    try {
      if (!error) timeline = calculateTimeline(this.props.images, options);
    } catch (failure) {
      error = failure.message;
    }
    const model = { ...plan, timeline, error };
    this.timelineCache = {
      compositionMode: this.props.compositionMode,
      images: this.props.images,
      durationInput: this.state.durationInput,
      model,
    };
    return model;
  };

  getTimeline = () => this.getTimelineModel().timeline;

  handleDurationChange = durationInput => {
    this.setState({ durationInput });
    this.props.onOutputOptionsChange({ durationInput, playbackMode: this.state.playbackMode });
  };
  handlePlaybackModeChange = playbackMode => {
    this.setState({ playbackMode });
    this.props.onOutputOptionsChange({ durationInput: this.state.durationInput, playbackMode });
  };

  handleTimeChange = time => {
    const starts = timelineStartTimes(this.getTimeline());
    let index = starts.findIndex(start => start > time) - 1;
    if (index < 0) index = starts.length - 1;
    this.setState({ absoluteCurrentIndex: Math.max(0, index), isPlaying: false, playbackEnded: false }, this.updateCanvas);
  };

  getCurrentFrames = () => {
    const timeline = this.getTimeline();
    const globalFrame = timeline[this.state.absoluteCurrentIndex] || timeline[0];

    if (!globalFrame) {
      return this.props.images.map((image, index) => image.frames[this.getTimelineModel().tracks[index].order[0]]);
    }

    return this.props.images.map((image, index) => {
      const frameIndex = globalFrame.imageIndices[index];
      return image.frames[frameIndex];
    });
  };

  getCurrentMaxDelay = () => {
    const timeline = this.getTimeline();
    const globalFrame = timeline[this.state.absoluteCurrentIndex] || timeline[0];
    return globalFrame ? globalFrame.delay : 0;
  };

  getMaxFrames = () => Math.max(1, this.getTimeline().length);

  handlePlayClick = () => {
    const restarting = this.state.playbackEnded;
    this.setState(prev => ({ isPlaying: !prev.isPlaying,
      ...(prev.playbackEnded ? { absoluteCurrentIndex: 0, playbackEnded: false } : {}) }),
    restarting ? this.updateCanvas : undefined);
  };

  playsOnce = () => this.state.playbackMode === 'once' ||
    (this.state.playbackMode === 'auto' && this.getTimelineModel().hasFinite);

  isPlaying = () => {
    return this.props.canPlay && this.state.isPlaying;
  };

  handleImageInteraction = (name, isHovering, isDragging) => {
    this.setState(prev => ({
      hoveredImageName: isHovering ? name : prev.hoveredImageName === name ? null : prev.hoveredImageName,
      draggedImageName: isDragging ? name : prev.draggedImageName === name ? null : prev.draggedImageName,
    }));
  };

  handleDragEnd = (overlayX, overlayY, name) => {
    this.props.onImageChangeStart();
    const index = this.props.images.findIndex((image) => image.name === name);
    const image = this.props.images[index];
    this.props.onImageChange([
      ...this.props.images.slice(0, index),
      {
        ...image,
        // We're really moving the mix and min y around, so we need to convert that in terms
        // of the images "absolute" position change
        x: image.x + (overlayX - this.getMinX(image)),
        y: image.y + (overlayY - this.getMinY(image)),
      },
      ...this.props.images.slice(index),
    ]);
  };

  getImageDimensions = (image) => {
    if (this.imageDimensionsCache.has(image)) {
      return this.imageDimensionsCache.get(image);
    }

    const minX = Math.min(...image.frames.map((frame) => image.x + frame.x));
    const minY = Math.min(...image.frames.map((frame) => image.y + frame.y));
    const maxX = Math.max(...image.frames.map((frame) => image.x + frame.x + frame.width));
    const maxY = Math.max(...image.frames.map((frame) => image.y + frame.y + frame.height));

    const width = maxX - minX;
    const height = maxY - minY;

    const dimensions = { minX, minY, width, height };
    this.imageDimensionsCache.set(image, dimensions);
    return dimensions;
  };

  getMinX = (image) => {
    return this.getImageDimensions(image).minX;
  };

  getMinY = (image) => {
    return this.getImageDimensions(image).minY;
  };

  isSaveDialogOpen = () => this.props.canShowSaveDialog && this.state.shouldShowSaveDialog;

  handleSaveClick = () => this.setState({ shouldShowSaveDialog: true, saveError: null });

  handleSaveDialogClose = () => {
    this.exportController?.abort();
    this.exportController = null;
    this.setState({ shouldShowSaveDialog: false, isSaving: false });
  };

  handleTransparentColorChange = (transparentColor) => this.setState({ transparentColor });
  handleTransparencyToleranceChange = (transparencyTolerance) => this.setState({ transparencyTolerance });

  handleNameChange = (e) => this.setState({ saveImageName: e.target.value });

  handleKeepPreviousFramesChange = (e) => this.setState({ keepPreviousFrames: e.target.checked });

  handleNextFrameClick = () =>
    this.setState(
      (prevState) => ({
        absoluteCurrentIndex: mod(prevState.absoluteCurrentIndex + 1, this.getMaxFrames()),
        isPlaying: false,
        playbackEnded: false,
      }),
      () => {
        this.updateCanvas();
      }
    );

  handlePreviousFrameClick = () =>
    this.setState(
      (prevState) => ({
        absoluteCurrentIndex: mod(prevState.absoluteCurrentIndex - 1, this.getMaxFrames()),
        isPlaying: false,
        playbackEnded: false,
      }),
      () => {
        this.updateCanvas();
      }
    );

  render() {
    return (
      <>
        <CanvasViewport
          width={this.props.width}
          height={this.props.height}
          canMoveImages={this.props.canMoveImages}
          isMoveMode={this.state.isMoveMode}
          onMoveModeChange={() => this.setState((prev) => ({ isMoveMode: !prev.isMoveMode }))}
        >
          {(scale, isCompact) => (
            <>
              <canvas
                data-testid="animated-canvas"
                width={this.props.width}
                height={this.props.height}
                onClick={this.props.onClick}
                onMouseMove={this.handleMouseMove}
                style={{
                  ...styles.canvas,
                  cursor: this.props.showEyeDropperCursor ? 'crosshair' : 'inherit',
                }}
                ref={this.canvasRef}
              />
              {this.props.canMoveImages && (!isCompact || this.state.isMoveMode) && (
                <DraggableOverlayLayer
                  scale={scale}
                  images={this.props.images}
                  width={this.props.width}
                  height={this.props.height}
                  onDragEnd={this.handleDragEnd}
                  onInteractionChange={this.handleImageInteraction}
                  getImageDimensions={this.getImageDimensions}
                />
              )}
            </>
          )}
        </CanvasViewport>
        {this.props.images.length > 0 && this.props.canShowPlayer && (
          <Player
            compositionMode={this.props.compositionMode}
            onCompositionModeChange={this.props.onCompositionModeChange}
            playbackEnded={this.state.playbackEnded}
            playbackMode={this.state.playbackMode}
            onPlaybackModeChange={this.handlePlaybackModeChange}
            playsOnce={this.playsOnce()}
            timeline={this.getTimeline()}
            onTimeChange={this.handleTimeChange}
            durationInput={this.state.durationInput}
            onDurationChange={this.handleDurationChange}
            timelineError={this.getTimelineModel().error}
            shortened={this.getTimelineModel().shortened}
            images={this.props.images}
            highlightedImageName={this.state.draggedImageName || this.state.hoveredImageName}
            onClickPlay={this.handlePlayClick}
            onClickSave={this.handleSaveClick}
            onFrameChange={this.props.onStartIndexChange}
            totalNumFrames={this.getMaxFrames()}
            absoluteCurrentIndex={this.state.absoluteCurrentIndex}
            isPlaying={this.isPlaying()}
            disableButtons={this.state.isSaving}
            onUndoClick={this.props.onUndoClick}
            onRedoClick={this.props.onRedoClick}
            canUndo={this.props.canUndo}
            canRedo={this.props.canRedo}
            onClickNext={this.handleNextFrameClick}
            onClickPrevious={this.handlePreviousFrameClick}
            onFrameClick={this.props.onFrameClick}
          />
        )}
        <SaveDialog
          isOpen={this.isSaveDialogOpen()}
          onClickSave={this.handleSaveSubmit}
          onClose={this.handleSaveDialogClose}
          onTransparentColorChange={this.handleTransparentColorChange}
          onNameChange={this.handleNameChange}
          onEyeDropperClick={this.props.onEyeDropperClick}
          currentTransparentColor={this.state.transparentColor}
          transparencyTolerance={this.state.transparencyTolerance}
          onTransparencyToleranceChange={this.handleTransparencyToleranceChange}
          keepPreviousFrames={this.state.keepPreviousFrames}
          onKeepPreviousFramesChange={this.handleKeepPreviousFramesChange}
          isLoading={this.state.isSaving}
          progress={this.state.saveProgress}
          error={this.state.saveError}
          preview={<GifPreview
            images={this.props.images}
            timeline={this.getTimeline()}
            width={this.props.width}
            height={this.props.height}
            backgroundColor={this.props.backgroundColor}
            transparentColor={this.state.transparentColor}
            transparencyTolerance={this.state.transparencyTolerance}
            keepPreviousFrames={this.state.keepPreviousFrames}
            repeat={this.playsOnce() ? -1 : 0}
          />}
        />
      </>
    );
  }
}

AnimatedCanvas.propTypes = {
  compositionMode: PropTypes.oneOf(['source', 'repeat', 'truncate']),
  onCompositionModeChange: PropTypes.func,
  initialOutputOptions: PropTypes.shape({ durationInput: PropTypes.string, playbackMode: PropTypes.string }),
  onOutputOptionsChange: PropTypes.func,
  images: PropTypes.arrayOf(imageShape).isRequired,
  onClick: PropTypes.func,
  onMouseMove: PropTypes.func,
  onStartIndexChange: PropTypes.func,
  showSaveButton: PropTypes.bool,
  canShowSaveDialog: PropTypes.bool,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  backgroundColor: PropTypes.string.isRequired,
  canPlay: PropTypes.bool,
  initialIsPlaying: PropTypes.bool,
  onPlaybackChange: PropTypes.func,
  canMoveImages: PropTypes.bool,
  onImageChange: PropTypes.func,
  onImageChangeStart: PropTypes.func,
  onUndoClick: PropTypes.func,
  onRedoClick: PropTypes.func,
  onEyeDropperClick: PropTypes.func,
  canUndo: PropTypes.bool,
  canRedo: PropTypes.bool,
  selectedPixel: PropTypes.objectOf(PropTypes.number),
  showEyeDropperCursor: PropTypes.bool,
  onFrameClick: PropTypes.func,
  canShowPlayer: PropTypes.bool,
};

AnimatedCanvas.defaultProps = {
  initialOutputOptions: { durationInput: '', playbackMode: 'auto' },
  compositionMode: 'source',
  onCompositionModeChange: () => {},
  onOutputOptionsChange() {},
  onClick() {},
  onMouseMove() {},
  onStartIndexChange() {},
  onImageChange() {},
  onImageChangeStart() {},
  onEyeDropperClick() {},
  showSaveButton: false,
  canShowSaveDialog: false,
  canPlay: true,
  initialIsPlaying: true,
  onPlaybackChange() {},
  canMoveImages: true,
  onUndoClick() {},
  onRedoClick() {},
  canUndo: false,
  canRedo: false,
  selectedPixel: new Uint8ClampedArray(),
  showEyeDropperCursor: false,
  onFrameClick() {},
  canShowPlayer: true,
};

export default AnimatedCanvas;
